import React, { useState, useEffect } from 'react';
import {
  Search, FileText, BookOpen, Video, Archive, ExternalLink,
  Download, Star, Eye, CheckCircle2, Clock, Share2, X, Plus, Sparkles, BookCheck
} from 'lucide-react';
import { api } from '../services/api';
import { apiClient } from '../services/apiClient';
import { useAuth } from '../context/AuthContext';
import { useAlert } from '../context/AlertContext';

const levelOptions = [
  { id: 'all', label: 'All Levels' },
  { id: 'Class 6-12', label: 'Class 6-12' },
  { id: 'Undergraduate', label: 'Undergraduate' },
  { id: 'B.Tech', label: 'B.Tech' },
];

const libCategories = [
  { id: 'Research Papers', icon: FileText, label: 'Research Papers', count: '120+ Documents', color: '#9333ea' },
  { id: 'Study Guides', icon: BookOpen, label: 'Study Guides', count: '85+ Documents', color: '#2563eb' },
  { id: 'Video Lectures', icon: Video, label: 'Video Lectures', count: '40+ Hours', color: '#ec4899' },
  { id: 'Exam Archives', icon: Archive, label: 'Exam Archives', count: '10+ Years', color: '#10b981' },
];

/* ─── Resource Preview & Reader Modal ─── */
function ResourceReaderModal({ resource, onClose, onRate, onDownload }) {
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const [activeTab, setActiveTab] = useState('preview'); // 'preview' | 'topics' | 'details'
  const [userRating, setUserRating] = useState(0);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [loggedStudy, setLoggedStudy] = useState(false);

  if (!resource) return null;

  const iconMap = { pdf: FileText, video: Video, archive: Archive, book: BookOpen, course: Video, document: FileText };
  const Icon = iconMap[resource.type] || iconMap[resource.format] || FileText;

  const handleRatingClick = async (star) => {
    setUserRating(star);
    try {
      await onRate(resource.id || resource._id, star);
      setRatingSubmitted(true);
      showAlert('Rating Saved', `You gave ${star} stars to this resource!`);
    } catch {
      showAlert('Info', 'Rating recorded for this session.');
    }
  };

  const handleStudyLog = async () => {
    if (!user) {
      showAlert('Scholar Account Required', 'Please log in to record study hours and maintain your streak.');
      return;
    }
    try {
      await api.logActivity({
        skill: resource.subject || 'Academic Reading',
        activityType: 'library',
        durationSeconds: 25 * 60,
        refId: resource.id || resource._id,
        focusMode: 'deep_work',
        sessionCompleted: true,
      });
      setLoggedStudy(true);
      showAlert('Study Session Logged', 'Logged 25 mins of focused study! Your consistency score and streak have been updated.');
    } catch {
      setLoggedStudy(true);
      showAlert('Study Session Logged', 'Logged 25 mins of focused study for your daily streak!');
    }
  };

  const handleDownloadClick = (e) => {
    e.stopPropagation();
    onDownload(resource);
    showAlert('Download Started', `Downloading "${resource.title || resource.name}" (${resource.size || 'PDF'}).`);
    if (resource.url) {
      window.open(resource.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(resource.url || window.location.href);
      showAlert('Link Copied', 'Resource link copied to clipboard.');
    }
  };

  // Format video embed if applicable
  const isVideo = resource.format === 'video' || resource.type === 'video' || resource.type === 'course';
  const getEmbedUrl = (url) => {
    if (!url) return null;
    if (url.includes('youtube.com/watch?v=')) {
      return url.replace('watch?v=', 'embed/');
    }
    if (url.includes('youtu.be/')) {
      const id = url.split('youtu.be/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${id}`;
    }
    return url;
  };
  const embedUrl = isVideo ? getEmbedUrl(resource.url) : null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(5px)',
          zIndex: 1,
        }}
        onClick={onClose}
      />
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          maxWidth: '840px',
          width: '92vw',
          maxHeight: '88vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '1.5rem',
          background: '#ffffff',
          boxShadow: '0 25px 60px -12px rgba(0, 0, 0, 0.35)',
          animation: 'scaleIn 0.2s ease-out both',
        }}
      >
        
        {/* Header Bar */}
        <div style={{ padding: '1.5rem 2rem 1.25rem', borderBottom: '1px solid #e2e8f0', background: 'linear-gradient(135deg, #faf5ff 0%, #ffffff 100%)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ width: '3.25rem', height: '3.25rem', borderRadius: '1rem', background: 'linear-gradient(135deg, rgba(147,51,234,0.12), rgba(147,51,234,0.22))', border: '1.5px solid rgba(147,51,234,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon size={24} color="var(--primary-gold)" />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 800, padding: '0.2rem 0.6rem', borderRadius: '9999px', background: 'rgba(147,51,234,0.1)', color: 'var(--primary-gold)', border: '1px solid rgba(147,51,234,0.2)' }}>
                  {resource.subject || 'General'}
                </span>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: '9999px', background: '#f1f5f9', color: '#475569' }}>
                  {resource.level || 'All Levels'}
                </span>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: '9999px', background: 'rgba(16,185,129,0.1)', color: '#059669' }}>
                  {resource.format?.toUpperCase() || resource.type?.toUpperCase() || 'PDF'}
                </span>
              </div>
              <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--navy-dark)', lineHeight: 1.25, margin: 0 }}>
                {resource.title || resource.name}
              </h3>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button onClick={handleShare} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', cursor: 'pointer' }} title="Share Link">
              <Share2 size={16} />
            </button>
            <button onClick={onClose} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', cursor: 'pointer' }}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Quick Action Toolbar */}
        <div style={{ padding: '0.75rem 2rem', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.8rem', color: '#64748b' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontWeight: 600 }}>
              <Star size={15} color="#f59e0b" fill="#f59e0b" />
              {(resource.avgRating || 4.8).toFixed(1)} ({resource.ratingCount || 24} ratings)
            </span>
            <span>•</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <Download size={14} color="#64748b" />
              {resource.downloads || 150} downloads
            </span>
            <span>•</span>
            <span>Size: {resource.size || '3.5 MB'}</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {resource.url && (
              <a
                href={resource.url}
                target="_blank"
                rel="noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.85rem', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '0.625rem', color: 'var(--navy-dark)', fontSize: '0.8rem', fontWeight: 700, textDecoration: 'none', cursor: 'pointer' }}
              >
                <ExternalLink size={14} /> Open Source
              </a>
            )}
            <button
              onClick={handleDownloadClick}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.85rem', background: 'var(--primary-gold)', border: 'none', borderRadius: '0.625rem', color: '#ffffff', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px var(--gold-shadow)' }}
            >
              <Download size={14} /> Download File
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', padding: '0 2rem', background: '#ffffff' }}>
          {[
            { id: 'preview', label: isVideo ? 'Video & Lecture Notes' : 'Document Notes & Preview', icon: Eye },
            { id: 'topics', label: 'Key Topics & Concepts', icon: BookCheck },
            { id: 'details', label: 'About & Source', icon: BookOpen },
          ].map(t => {
            const TIcon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.875rem 1.25rem',
                  background: 'none', border: 'none',
                  borderBottom: active ? '2.5px solid var(--primary-gold)' : '2.5px solid transparent',
                  color: active ? 'var(--primary-gold)' : '#64748b',
                  fontWeight: active ? 800 : 600,
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s ease',
                }}
              >
                <TIcon size={16} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Modal Scrollable Body */}
        <div style={{ padding: '1.75rem 2rem', overflowY: 'auto', flex: 1, maxHeight: 'calc(90vh - 240px)' }}>
          {activeTab === 'preview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              
              {/* Embedded video player if video */}
              {isVideo && (
                <div style={{ width: '100%', borderRadius: '1rem', overflow: 'hidden', background: '#0f172a', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
                  {embedUrl && embedUrl.includes('embed') ? (
                    <iframe
                      src={embedUrl}
                      title={resource.title || 'Video Lecture'}
                      style={{ width: '100%', height: '100%', border: 'none' }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <div style={{ textAlign: 'center', color: 'white', padding: '2rem' }}>
                      <Video size={48} color="#c084fc" style={{ margin: '0 auto 1rem' }} />
                      <h4 style={{ color: 'white', marginBottom: '0.5rem' }}>Full HD Video Lecture</h4>
                      <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '1.25rem' }}>Streamed from the Ekalavya Academic CDN</p>
                      {resource.url && (
                        <a href={resource.url} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.25rem', background: 'var(--primary-gold)', color: 'white', borderRadius: '0.75rem', fontWeight: 700, textDecoration: 'none', fontSize: '0.85rem' }}>
                          <ExternalLink size={16} /> Watch on Provider Site
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Description banner */}
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '1rem', padding: '1.25rem' }}>
                <h4 style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--navy-dark)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Sparkles size={16} color="var(--primary-gold)" /> Summary & Learning Objectives
                </h4>
                <p style={{ color: '#475569', fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>
                  {resource.description || 'Curated academic material formatted for structured study, concept mastery, and exam revision.'}
                </p>
              </div>

              {/* Formatted Notes / Preview Text */}
              <div style={{ background: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: '1rem', padding: '1.5rem', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid #f1f5f9' }}>
                  <h4 style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--navy-dark)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FileText size={18} color="var(--primary-gold)" /> Document Content & Key Formulas
                  </h4>
                  <span style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: 700, background: 'rgba(16,185,129,0.1)', padding: '0.2rem 0.5rem', borderRadius: '0.5rem' }}>
                    ✓ Verified Notes
                  </span>
                </div>
                <div style={{ color: '#334155', fontSize: '0.9rem', lineHeight: 1.7, whiteSpace: 'pre-line', fontFamily: 'inherit' }}>
                  {resource.previewText || `### Key Formulas & High-Yield Principles\n- **Core Definition**: Fundamental laws and definitions governing ${resource.subject || 'this topic'}.\n- **Derivations & Proofs**: Step-by-step mathematical reductions with boundary conditions.\n- **Solved Examples**: Representative numerical problems with common exam pitfalls highlighted.\n- **Quick Revision**: Summarized cheat sheet with unit dimensions and boundary theorems.`}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'topics' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <h4 style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--navy-dark)', marginBottom: '0.5rem' }}>
                  Syllabus Breakdown & Topics
                </h4>
                <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1.25rem' }}>
                  Master each topic below. Check items to keep track of your revision progress.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {(resource.topics && resource.topics.length > 0 ? resource.topics : [
                    'Foundations & Core Axioms',
                    'Theoretical Proofs & Derivations',
                    'Practical Implementation & Pseudocode',
                    'Previous Year Exam Questions',
                    'Common Edge Cases & Pitfalls'
                  ]).map((t, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.875rem 1rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem' }}>
                      <CheckCircle2 size={18} color="#10b981" />
                      <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--navy-dark)' }}>{t}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'details' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>Contributed By</span>
                <p style={{ fontWeight: 700, color: 'var(--navy-dark)', margin: '0.25rem 0 0', fontSize: '0.9rem' }}>{resource.uploaded_by || 'Ekalavya Academic Faculty'}</p>
              </div>
              <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>Target Academic Tier</span>
                <p style={{ fontWeight: 700, color: 'var(--navy-dark)', margin: '0.25rem 0 0', fontSize: '0.9rem' }}>{resource.level || 'All Levels'}</p>
              </div>
              <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>File Size & Type</span>
                <p style={{ fontWeight: 700, color: 'var(--navy-dark)', margin: '0.25rem 0 0', fontSize: '0.9rem' }}>{resource.size || '3.5 MB'} ({resource.format || resource.type})</p>
              </div>
              <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>License & Access</span>
                <p style={{ fontWeight: 700, color: '#10b981', margin: '0.25rem 0 0', fontSize: '0.9rem' }}>100% Free / Open Academic</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions: Rate & Consistency Logging */}
        <div style={{ padding: '1.25rem 2rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          {/* 5-Star Rating */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569' }}>Rate resource:</span>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => handleRatingClick(star)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex' }}
                  title={`Rate ${star} star`}
                >
                  <Star size={18} color="#f59e0b" fill={(userRating || Math.round(resource.avgRating || 0)) >= star ? '#f59e0b' : 'none'} />
                </button>
              ))}
            </div>
            {ratingSubmitted && <span style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: 700 }}>Saved!</span>}
          </div>

          {/* Log 25m Focus Session button */}
          <button
            onClick={handleStudyLog}
            disabled={loggedStudy}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.625rem 1.1rem',
              background: loggedStudy ? 'rgba(16,185,129,0.1)' : 'rgba(147,51,234,0.08)',
              border: `1.5px solid ${loggedStudy ? '#10b981' : 'rgba(147,51,234,0.25)'}`,
              color: loggedStudy ? '#059669' : 'var(--primary-gold)',
              borderRadius: '0.75rem',
              fontWeight: 700, fontSize: '0.85rem',
              cursor: loggedStudy ? 'default' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <Clock size={16} />
            {loggedStudy ? '✓ 25m Study Session Logged' : 'Log 25m Study to Streak'}
          </button>
        </div>

      </div>
    </div>
  );
}

/* ─── Upload Resource Modal ─── */
function UploadResourceModal({ onClose, onCreated }) {
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('Computer Science');
  const [level, setLevel] = useState('B.Tech');
  const [category, setCategory] = useState('Study Guides');
  const [format, setFormat] = useState('pdf');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setLoading(true);
    try {
      const payload = {
        title,
        name: title,
        subject,
        level,
        category,
        format,
        type: format === 'video' ? 'video' : 'pdf',
        size: '2.5 MB',
        url: url.trim() || 'https://ekalavya.edu',
        description: description || 'Uploaded by student scholar for community revision.',
        uploaded_by: user?.full_name || 'Scholar Contributor',
      };
      const created = await apiClient.post('/notes', payload);
      onCreated(created);
      showAlert('Resource Shared', 'Thank you! Your notes are now published to the Digital Library.');
      onClose();
    } catch (err) {
      showAlert('Error', err.message || 'Failed to upload resource');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(5px)',
          zIndex: 1,
        }}
        onClick={onClose}
      />
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          maxWidth: '540px',
          width: '92vw',
          padding: '2rem',
          borderRadius: '1.5rem',
          background: '#ffffff',
          boxShadow: '0 25px 60px -12px rgba(0, 0, 0, 0.35)',
          animation: 'scaleIn 0.2s ease-out both',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <div>
            <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--navy-dark)', margin: 0 }}>Upload Academic Notes</h3>
            <p style={{ fontSize: '0.82rem', color: '#64748b', margin: '0.25rem 0 0' }}>Share your verified study materials with peers across India</p>
          </div>
          <button onClick={onClose} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', cursor: 'pointer' }}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '0.35rem' }}>Document Title *</label>
            <input
              type="text" required placeholder="e.g. Data Structures & Algorithms Revision Notes"
              value={title} onChange={e => setTitle(e.target.value)}
              style={{ width: '100%', padding: '0.75rem 1rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', outline: 'none', fontSize: '0.9rem', fontFamily: 'inherit' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '0.35rem' }}>Subject</label>
              <select value={subject} onChange={e => setSubject(e.target.value)} style={{ width: '100%', padding: '0.75rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', outline: 'none', fontSize: '0.85rem', fontFamily: 'inherit' }}>
                {['Computer Science', 'Physics', 'Mathematics', 'Chemistry', 'AI / ML', 'Biology', 'Electronics'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '0.35rem' }}>Academic Level</label>
              <select value={level} onChange={e => setLevel(e.target.value)} style={{ width: '100%', padding: '0.75rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', outline: 'none', fontSize: '0.85rem', fontFamily: 'inherit' }}>
                {['Class 6-12', 'B.Tech', 'Undergraduate', 'All Levels'].map(l => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '0.35rem' }}>Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)} style={{ width: '100%', padding: '0.75rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', outline: 'none', fontSize: '0.85rem', fontFamily: 'inherit' }}>
                {['Study Guides', 'Research Papers', 'Video Lectures', 'Exam Archives'].map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '0.35rem' }}>Format</label>
              <select value={format} onChange={e => setFormat(e.target.value)} style={{ width: '100%', padding: '0.75rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', outline: 'none', fontSize: '0.85rem', fontFamily: 'inherit' }}>
                <option value="pdf">PDF Document</option>
                <option value="video">Video Lecture</option>
                <option value="archive">Exam Archive</option>
              </select>
            </div>
          </div>

          <div>
            <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '0.35rem' }}>PDF / Video URL</label>
            <input
              type="url" placeholder="https://drive.google.com/... or https://youtube.com/..."
              value={url} onChange={e => setUrl(e.target.value)}
              style={{ width: '100%', padding: '0.75rem 1rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', outline: 'none', fontSize: '0.9rem', fontFamily: 'inherit' }}
            />
          </div>

          <div>
            <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '0.35rem' }}>Summary / Highlights</label>
            <textarea
              rows={3} placeholder="Brief summary of key topics covered in these notes..."
              value={description} onChange={e => setDescription(e.target.value)}
              style={{ width: '100%', padding: '0.75rem 1rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', outline: 'none', fontSize: '0.9rem', fontFamily: 'inherit', resize: 'vertical' }}
            />
          </div>

          <button
            type="submit" disabled={loading}
            style={{ width: '100%', padding: '0.875rem', background: 'var(--primary-gold)', color: '#ffffff', border: 'none', borderRadius: '0.75rem', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', marginTop: '0.5rem', boxShadow: '0 4px 14px var(--gold-shadow)' }}
          >
            {loading ? 'Publishing...' : 'Publish to Digital Library'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─── Resource Row ─── */
function ResourceRow({ r, onSelect, onOpenModal }) {
  const iconMap = { pdf: FileText, video: Video, archive: Archive, book: BookOpen, course: Video, document: FileText };
  const Icon = iconMap[r.type] || iconMap[r.format] || FileText;

  return (
    <div
      onClick={() => onOpenModal(r)}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '1.15rem 1.25rem', background: '#ffffff', borderRadius: '1rem',
        border: '1.5px solid #e2e8f0', transition: 'all 0.22s cubic-bezier(0.25, 1, 0.5, 1)',
        cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = 'var(--primary-gold)';
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(147,51,234,0.08)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = '#e2e8f0';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.02)';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '1.15rem', flex: 1, minWidth: 0 }}>
        <div style={{ width: '3rem', height: '3rem', background: 'rgba(147, 51, 234, 0.07)', borderRadius: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(147, 51, 234, 0.15)', flexShrink: 0 }}>
          <Icon size={20} color="var(--primary-gold)" />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h5 style={{ color: 'var(--navy-dark)', fontWeight: 700, fontSize: '0.98rem', marginBottom: '0.35rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.title || r.name}
          </h5>
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {r.subject && (
              <span style={{ background: '#f1f5f9', color: '#475569', fontSize: '0.75rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: '0.375rem' }}>
                {r.subject}
              </span>
            )}
            {r.level && (
              <span style={{ color: 'var(--primary-gold)', fontSize: '0.75rem', fontWeight: 700, background: 'rgba(147,51,234,0.08)', padding: '0.15rem 0.5rem', borderRadius: '0.375rem' }}>
                {r.level}
              </span>
            )}
            {r.size && <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>{r.size}</span>}
            {r.uploaded_by && <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>• {r.uploaded_by}</span>}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', color: '#d97706', fontSize: '0.75rem', fontWeight: 700 }}>
              <Star size={12} fill="#f59e0b" color="#f59e0b" /> {(r.avgRating || 4.8).toFixed(1)}
            </span>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexShrink: 0, marginLeft: '1rem' }}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onOpenModal(r);
          }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
            padding: '0.5rem 1rem', background: 'rgba(147,51,234,0.08)',
            border: '1.5px solid rgba(147,51,234,0.25)', borderRadius: '0.625rem',
            color: 'var(--primary-gold)', fontWeight: 700, fontSize: '0.8rem',
            cursor: 'pointer', transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--primary-gold)'; e.currentTarget.style.color = '#ffffff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(147,51,234,0.08)'; e.currentTarget.style.color = 'var(--primary-gold)'; }}
        >
          <Eye size={14} /> Open & Read
        </button>

        {r.url && (
          <a
            href={r.url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ width: '2.25rem', height: '2.25rem', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', borderRadius: '0.625rem', color: '#64748b', border: '1px solid var(--slate-200)', transition: 'all 0.15s' }}
            title="Open external source"
            onMouseEnter={e => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = 'var(--navy-dark)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#64748b'; }}
          >
            <ExternalLink size={14} />
          </a>
        )}
      </div>
    </div>
  );
}

/* ─── Main Digital Library Page ─── */
export default function Library() {
  const [resources, setResources] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeLevel, setActiveLevel] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const loadResources = async () => {
    try {
      const data = await api.getCollection('library_resources');
      setResources(data || []);
      setFiltered(data || []);
    } catch {
      setResources([]);
      setFiltered([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResources();
  }, []);

  useEffect(() => {
    let result = resources;
    if (activeLevel !== 'all') {
      result = result.filter(r => r.level === activeLevel);
    }
    if (selectedCategory) {
      result = result.filter(r => r.category === selectedCategory || (selectedCategory === 'Video Lectures' && (r.type === 'video' || r.format === 'video')));
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(r =>
        r.title?.toLowerCase().includes(q) ||
        r.name?.toLowerCase().includes(q) ||
        r.subject?.toLowerCase().includes(q) ||
        r.category?.toLowerCase().includes(q) ||
        r.description?.toLowerCase().includes(q)
      );
    }
    setFiltered(result);
  }, [search, activeLevel, selectedCategory, resources]);

  const handleRateResource = async (id, rating) => {
    await apiClient.post(`/notes/${id}/rate`, { rating });
    loadResources();
  };

  const handleDownloadResource = (res) => {
    // Optional: increment downloads locally
    setResources(prev => prev.map(item => (item.id === res.id ? { ...item, downloads: (item.downloads || 0) + 1 } : item)));
  };

  const handleResourceCreated = (newRes) => {
    setResources(prev => [newRes, ...prev]);
  };

  return (
    <div>
      {/* Top Header & Search Bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1.5rem', marginBottom: '2.5rem' }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'rgba(147,51,234,0.08)', border: '1px solid rgba(147,51,234,0.18)', borderRadius: '9999px', padding: '0.3rem 0.85rem', color: 'var(--primary-gold)', fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>
            <Sparkles size={14} /> Open Academic Repository
          </div>
          <h2 style={{ fontSize: '2.25rem', fontWeight: 800, color: 'var(--navy-dark)', marginBottom: '0.5rem', letterSpacing: '-0.02em' }}>
            Digital Library & Archives
          </h2>
          <p className="secondary-header-text" style={{ maxWidth: '640px' }}>
            Instant access to verified research papers, handwritten revision guides, previous year question banks, and video lectures.
          </p>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.85rem', alignItems: 'center' }}>
          <button
            onClick={() => setShowUploadModal(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.625rem 1.15rem', background: 'linear-gradient(135deg, var(--primary-gold), var(--primary-gold-deep))',
              color: '#ffffff', border: 'none', borderRadius: '0.75rem', fontWeight: 700, fontSize: '0.875rem',
              cursor: 'pointer', boxShadow: '0 4px 14px var(--gold-shadow)', transition: 'all 0.2s',
            }}
          >
            <Plus size={16} /> Upload Notes
          </button>

          <select
            value={activeLevel}
            onChange={e => setActiveLevel(e.target.value)}
            style={{ background: '#ffffff', border: '1.5px solid var(--slate-200)', color: 'var(--navy-dark)', borderRadius: '0.75rem', padding: '0.625rem 1rem', fontSize: '0.875rem', fontWeight: 600, outline: 'none', fontFamily: 'inherit', cursor: 'pointer', minWidth: '150px' }}
          >
            {levelOptions.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>

          <div style={{ position: 'relative' }}>
            <Search size={16} color="rgba(0,0,0,0.35)" style={{ position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder="Search subjects, notes, topics…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ background: '#ffffff', border: '1.5px solid var(--slate-200)', color: 'var(--navy-dark)', borderRadius: '0.75rem', padding: '0.625rem 1rem 0.625rem 2.4rem', fontSize: '0.875rem', outline: 'none', width: '17rem', fontFamily: 'inherit' }}
              onFocus={e => e.target.style.borderColor = 'var(--primary-gold)'}
              onBlur={e => e.target.style.borderColor = 'var(--slate-200)'}
            />
          </div>
        </div>
      </div>

      {/* Category Filter Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1.25rem', marginBottom: '2.5rem' }}>
        {libCategories.map(({ id, icon: Icon, label, count, color }) => {
          const isSelected = selectedCategory === id;
          return (
            <div
              key={id}
              onClick={() => setSelectedCategory(isSelected ? null : id)}
              className="glass-card"
              style={{
                cursor: 'pointer', position: 'relative', overflow: 'hidden', transition: 'all 0.25s',
                padding: '1.25rem 1.25rem', borderRadius: '1rem', background: isSelected ? 'rgba(147,51,234,0.06)' : '#ffffff',
                border: isSelected ? `2px solid ${color}` : '1.5px solid #e2e8f0',
                transform: isSelected ? 'translateY(-3px)' : 'translateY(0)',
                boxShadow: isSelected ? `0 10px 25px ${color}20` : '0 2px 8px rgba(0,0,0,0.02)',
              }}
            >
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: isSelected ? color : 'transparent', transition: 'background 0.2s' }} />
              <div style={{ width: '2.75rem', height: '2.75rem', borderRadius: '0.75rem', background: `${color}15`, border: `1px solid ${color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.85rem' }}>
                <Icon size={22} color={color} />
              </div>
              <h4 style={{ color: isSelected ? color : 'var(--navy-dark)', fontWeight: 800, fontSize: '1rem', marginBottom: '0.2rem' }}>{label}</h4>
              <p style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 800, letterSpacing: '0.08em', margin: 0 }}>
                {isSelected ? '✓ Active Filter' : count}
              </p>
            </div>
          );
        })}
      </div>

      {/* Main Resources List Card */}
      <div className="glass-card" style={{ padding: '2rem', background: '#ffffff', borderRadius: '1.25rem', border: '1.5px solid #e2e8f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.75rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h3 style={{ fontWeight: 800, fontSize: '1.3rem', color: 'var(--navy-dark)', margin: 0 }}>
              {selectedCategory ? `${selectedCategory} (${filtered.length})` : `All Verified Resources (${filtered.length})`}
            </h3>
            <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '0.2rem 0 0' }}>
              Click on any document row to preview, read full content, watch lectures, or download
            </p>
          </div>
          {selectedCategory && (
            <button
              onClick={() => setSelectedCategory(null)}
              style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--primary-gold)', background: 'rgba(147,51,234,0.08)', border: '1px solid rgba(147,51,234,0.2)', padding: '0.35rem 0.8rem', borderRadius: '0.5rem', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Reset Category Filter
            </button>
          )}
        </div>

        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', background: '#f8fafc', borderRadius: '1rem', color: '#64748b', fontSize: '0.9rem', gap: '0.75rem' }}>
            <img src="/Logo.jpeg" alt="Loading" style={{ width: 32, height: 32, borderRadius: 8, animation: 'pulseSoft 1.5s infinite' }} />
            <span>Loading curated academic repository...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem 2rem', background: '#f8fafc', borderRadius: '1rem', border: '1px dashed #cbd5e1' }}>
            <BookOpen size={40} color="#94a3b8" style={{ margin: '0 auto 0.75rem' }} />
            <h4 style={{ color: 'var(--navy-dark)', fontWeight: 700, marginBottom: '0.35rem' }}>No resources match your filters</h4>
            <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0 }}>Try clearing the search query or changing the category/level filter.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {filtered.map(r => (
              <ResourceRow
                key={r.id || r._id}
                r={r}
                onOpenModal={setSelectedResource}
              />
            ))}
          </div>
        )}
      </div>

      {/* Reader & Preview Modal */}
      {selectedResource && (
        <ResourceReaderModal
          resource={selectedResource}
          onClose={() => setSelectedResource(null)}
          onRate={handleRateResource}
          onDownload={handleDownloadResource}
        />
      )}

      {/* Upload Resource Modal */}
      {showUploadModal && (
        <UploadResourceModal
          onClose={() => setShowUploadModal(false)}
          onCreated={handleResourceCreated}
        />
      )}
    </div>
  );
}
